import * as React from 'react';
import { Grid, useMediaQuery } from '@material-ui/core';
import { EMPTY_TEXT, Magnetic as Mi, MimeType } from '@mitchell/react.component.ux';
import { TranslationService } from "../../common";
import { IntelligentPhotoCaptureEstimatorContentType, IntelligentPhotoCaptureViewType } from '../enums';
import { IntelligentAlbumHierarchy, IntelligentAssignmentSummary } from '../constants';
import { attachmentInfo, getDefaultGalleryProps, normalizeAlbumsByVinOption, sortAlbumTilesForDisplay } from '../utilities';
import { IntelligentPhotoCaptureArtifactsCategoryNames } from '../enums';
import { AlbumGroup } from './AlbumGroup';
import { NestedTwisty } from './NestedTwisty';
import { StyledRecursiveRenderer } from './AlbumHierarchyComponent.style';
import { EmptyAlbumMessageComponent } from './EmptyAlbumMessageComponent';
import * as utilities from '../utilities';
/**
 * IntelligentAlbumHierarchyComponent - Renders albums and their nested subalbums in a hierarchical structure
 *
 * This component recursively renders albums and their subalbums using a twisty (expandable/collapsible)
 * interface. It maintains expansion state for each album independently.
 */
export const AlbumHierarchyComponent = ({ photoCaptureAttachmentInfo, countryCode, isDownloadComplete, screenName, albums, albumDocIds, isChild = false, indentLevel = 0, albumOrganizerData, shouldDisplayDefaultCategory, photoCaptureVehicle, isConfidence, isSelectModeOn, onTileClick, clearSelectionTrigger = 0, onViewChange, prtcDetailsData, userVehicleInfo, vinOption }) => {
    const translateFn = TranslationService.translate;
    const isSubLabelMaxWidthReached = useMediaQuery(`(max-width: ${Mi.layoutBreakpointSm})`);
    const isSummaryScreen = screenName === IntelligentAssignmentSummary.SUMMARY;
    const [expandedState, setExpandedState] = React.useState({});
    const [albumIds, setAlbumIds] = React.useState([]);
    const [galleryProps, setGalleryProps] = React.useState(getDefaultGalleryProps());
    const galleryPropsRef = React.useRef(galleryProps);
    React.useEffect(() => {
        galleryPropsRef.current = galleryProps;
    }, [galleryProps]);
    /**
     * Helper function to recursively set expansion state for all albums and their subalbums
     * @param albumsToExpand - The albums to process
     */
    const setAllExpanded = (albumsToExpand) => {
        const initialState = {};
        albumsToExpand.forEach((album) => {
            initialState[album.id] = true;
            if (album.subAlbums && album.subAlbums.length > 0) {
                setAllExpanded(album.subAlbums);
            }
        });
        setExpandedState(initialState);
    };
    /**
     * Recursively updates the selection state of tiles within a nested album structure.
     * @param albums - The array of albums containing tiles and potentially subalbums
     * @param updater - A callback function that determines how each tile should be transformed
     * @returns {IPhotoCaptureNestedAlbum} - A new array of albums with updated tiles, preserving the original structure
     */
    const toggleTileSelectionState = (albums, updater) => {
        return albums.map((album) => (Object.assign(Object.assign({}, album), { tiles: album.tiles.map((tile) => updater(tile)), subAlbums: album.subAlbums &&
                album.subAlbums.map((subAlbum) => (Object.assign(Object.assign({}, subAlbum), { tiles: subAlbum.tiles.map((tile) => updater(tile)) }))) })));
    };
    /**
     * Handles tile click events by toggling the selection state of the clicked tile.
     * @param tileId - The ID of the tile that was clicked
     * @param isSelected - The new selection state to apply to the tile
     * @returns {void}
     */
    const handleOnTileClick = (tileId, isSelected) => {
        onTileClick && onTileClick(tileId, isSelected);
        if (isSelectModeOn) {
            setGalleryProps((prevProps) => (Object.assign(Object.assign({}, prevProps), { albums: toggleTileSelectionState(prevProps.albums, (tile) => tile.id === tileId ? Object.assign(Object.assign({}, tile), { isSelected }) : tile) })));
        }
        else {
            const latestGalleryProps = galleryPropsRef.current;
            const selectedTile = latestGalleryProps.albums
                .flatMap((album) => album.tiles)
                .find((tile) => tile.id === tileId);
            const selectedAlbum = latestGalleryProps.albums.find((album) => album.tiles.some((tile) => tile.id === tileId) &&
                album.folderTypeCode === IntelligentPhotoCaptureArtifactsCategoryNames.COLLECTING_ARTIFACTS);
            const isCollectingArtifactsTile = !!selectedAlbum;
            // Only call onViewChange if the tile's mimeType is an image type and it's not in the COLLECTING_ARTIFACTS category, otherwise do nothing on tile click
            if (selectedTile &&
                !isCollectingArtifactsTile &&
                attachmentInfo.isImageMimeType(selectedTile.mimeType || MimeType.IMAGE_JPEG)) {
                onViewChange(IntelligentPhotoCaptureViewType.ESTIMATOR_GALLERY, EMPTY_TEXT, tileId);
            }
        }
    };
    /**
     * Handles edit icon click events by navigating to the VIN decode view for the selected tile.
     * @param attachmentId - The ID of the attachment associated with the clicked edit icon
     * @returns {void}
     */
    const handleOnEditIconClick = (attachmentId) => {
        const latestGalleryProps = galleryPropsRef.current;
        const selectedTile = latestGalleryProps.albums
            .flatMap((album) => album.tiles)
            .find((tile) => tile.id === attachmentId);
        const selectedContentType = selectedTile && selectedTile.contentType;
        onViewChange(selectedContentType === IntelligentPhotoCaptureEstimatorContentType.VIN ||
            selectedContentType === IntelligentPhotoCaptureEstimatorContentType.VIN_STICKER
            ? IntelligentPhotoCaptureViewType.ESTIMATOR_VIN_DECODE
            : IntelligentPhotoCaptureViewType.ESTIMATOR_ODOMETER_LICENSE_PLATE_EDIT, EMPTY_TEXT, attachmentId, selectedContentType);
    };
    /**
     * Deselects all artifacts (tiles) across all albums in the gallery.
     * @returns {void}
     */
    const handleDeselectArtifacts = () => {
        setGalleryProps((prevProps) => (Object.assign(Object.assign({}, prevProps), { albums: toggleTileSelectionState(prevProps.albums, (tile) => (Object.assign(Object.assign({}, tile), { isSelected: false }))) })));
    };
    React.useEffect(() => {
        let albumsResultSet = {
            albums: albums || [],
            albumDocIds: albumDocIds || []
        };
        if (!(albums === null || albums === void 0 ? void 0 : albums.length)) {
            albumsResultSet = utilities.albumOrganizer.organizeImagesIntoAlbums(photoCaptureAttachmentInfo.flat(), countryCode, screenName, shouldDisplayDefaultCategory, photoCaptureVehicle, isConfidence, prtcDetailsData, userVehicleInfo);
        }
        const sortedAlbums = normalizeAlbumsByVinOption(sortAlbumTilesForDisplay(albumsResultSet.albums, countryCode), vinOption, translateFn);
        const sortedAlbumsResultSet = Object.assign(Object.assign({}, albumsResultSet), { albums: sortedAlbums });
        setGalleryProps((prevProps) => (Object.assign(Object.assign({}, prevProps), { isSelectModeOn, panelBackgroundColor: isSummaryScreen && isSelectModeOn ? Mi.colorWhite : EMPTY_TEXT, albums: sortedAlbumsResultSet.albums, onTileClick: handleOnTileClick, onEditIconClick: handleOnEditIconClick })));
        setAllExpanded(sortedAlbumsResultSet.albums);
        setAlbumIds(sortedAlbumsResultSet.albumDocIds);
        albumOrganizerData === null || albumOrganizerData === void 0 ? void 0 : albumOrganizerData(sortedAlbumsResultSet);
    }, [
        photoCaptureAttachmentInfo,
        albums,
        albumDocIds,
        countryCode,
        screenName,
        shouldDisplayDefaultCategory,
        photoCaptureVehicle,
        isConfidence,
        prtcDetailsData,
        userVehicleInfo,
        vinOption,
        translateFn
    ]);
    React.useEffect(() => {
        setGalleryProps((prevProps) => (Object.assign(Object.assign({}, prevProps), { isSelectModeOn, panelBackgroundColor: isSummaryScreen && isSelectModeOn ? Mi.colorWhite : EMPTY_TEXT, onTileClick: handleOnTileClick, onEditIconClick: handleOnEditIconClick })));
    }, [isSelectModeOn, isSummaryScreen]);
    React.useEffect(() => {
        handleDeselectArtifacts();
    }, [clearSelectionTrigger]);
    React.useEffect(() => {
        if (isDownloadComplete) {
            handleDeselectArtifacts();
        }
    }, [isDownloadComplete]);
    /**
     * Toggle the expansion state of a specific album
     * @param albumId - The ID of the album to toggle
     */
    const toggleCollapse = (albumId) => {
        setExpandedState(prevState => (Object.assign(Object.assign({}, prevState), { [albumId]: !prevState[albumId] })));
    };
    return (React.createElement(StyledRecursiveRenderer, { id: `${IntelligentAlbumHierarchy.ID_ALBUM_HIERARCHY}_${indentLevel}`, "data-testid": `${IntelligentAlbumHierarchy.ID_ALBUM_HIERARCHY}_${indentLevel}`, isBottomMargin: isSummaryScreen && !indentLevel }, galleryProps.albums
        .filter((album) => album.shouldShowAlbum)
        .map((album) => (React.createElement(NestedTwisty, { key: album.id, id: album.id, label: album.header, headingSubLabel: !isSubLabelMaxWidthReached ? album.subHeader : undefined, isExpanded: expandedState[album.id] !== false, onToggle: () => toggleCollapse(album.id), isChildTwisty: isChild, indentLevel: indentLevel, shouldExpandByDefault: true, "data-testid": `${IntelligentAlbumHierarchy.ID_ALBUM_HIERARCHY_TWISTY}_${album.id}` }, !(album.tiles.length || album.subAlbums.length) ? (React.createElement(EmptyAlbumMessageComponent, null)) : (React.createElement(React.Fragment, null,
        !!album.tiles.length && (React.createElement(AlbumGroup, Object.assign({}, galleryProps, { translateFn: translateFn, id: album.id, tiles: album.tiles }))),
        !!album.subAlbums.length && (React.createElement(Grid, { container: true, direction: "column", "data-testid": `${IntelligentAlbumHierarchy.ID_ALBUM_HIERARCHY_SUBALBUMS_GROUP}_-${album.id}` },
            React.createElement(AlbumHierarchyComponent, { photoCaptureAttachmentInfo: photoCaptureAttachmentInfo, countryCode: countryCode, isDownloadComplete: isDownloadComplete, screenName: screenName, albums: album.subAlbums, albumDocIds: albumIds, isSelectModeOn: isSelectModeOn, onTileClick: onTileClick, clearSelectionTrigger: clearSelectionTrigger, shouldDisplayDefaultCategory: shouldDisplayDefaultCategory, isChild: true, indentLevel: indentLevel + 1, "data-testid": `${IntelligentAlbumHierarchy.ID_ALBUM_HIERARCHY_NESTED_RENDERER}_${album.id}`, photoCaptureVehicle: photoCaptureVehicle, onViewChange: onViewChange, prtcDetailsData: prtcDetailsData, userVehicleInfo: userVehicleInfo }))))))))));
};
//# sourceMappingURL=AlbumHierarchyComponent.js.map